import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, Input, NotesSection, Note } from '../components';
import { MapLocationPicker } from '../components/MapLocationPicker';
import { DataListFieldRenderer } from '../components/data-lists/DataListFieldRenderer';
import { Button, CurrencyPicker } from '../components/design-system';
import { useLocale } from '../contexts/LocaleContext';
import { useAppStore } from '../store/appStore';

// Mirrors MapLocationPicker's own (unexported) PickedLocation shape.
interface PickedLocation {
  latitude: number;
  longitude: number;
  address: string;
  country?: string;
  city?: string;
}
import { apiService, DataListColumn } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';

interface VendorFormScreenProps {
  /** Present = edit; absent = create. */
  vendorId?: string;
}

interface VendorAttachment {
  name: string;
  type: string;
  size: number;
  url: string;
  uploadedAt: string;
}

/** Backend stores `notes` as a single string; the mobile app JSON-serializes an
 * array of authored Note entries into it (mirrors QuotationFormScreen's and
 * AddOrderScreen's exact convention) so NotesSection can be reused here. */
const parseNotes = (raw: any): Note[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((note: any) => (typeof note === 'string'
      ? { content: note, createdBy: { id: '', email: '' }, createdAt: new Date() }
      : { ...note, createdAt: note.createdAt ? new Date(note.createdAt) : new Date() }));
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((note: any) => ({ ...note, createdAt: note.createdAt ? new Date(note.createdAt) : new Date() }));
      }
    } catch {
      return [{ content: raw, createdBy: { id: '', email: '' }, createdAt: new Date() }];
    }
  }
  return [];
};

export const VendorFormScreen: React.FC<VendorFormScreenProps> = ({ vendorId }) => {
  const isEdit = !!vendorId;
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { navigateTo, activeTab, setActiveTab, showNotification, routeParams } = useAppContext();
  const { user, currentBusiness } = useAppStore();
  const { t } = useLocale();
  // Set when this vendor was reached via a system Vendors Data List's scoped
  // mode (see VendorsListScreen.tsx/VendorDetailScreen.tsx) — enables the
  // "Extra Fields" section below on edit, and links a newly-created vendor
  // to that list on create. Mirrors ProductEditScreen.tsx/
  // ProductCreateScreen.tsx's identical pattern exactly.
  const listId: string | undefined = routeParams?.listId;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [location, setLocation] = useState<PickedLocation | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [crNumber, setCrNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  // Minimum order value for this vendor's products (blank = no minimum).
  const [minimumOrderAmount, setMinimumOrderAmount] = useState('');
  const [minimumOrderCurrency, setMinimumOrderCurrency] = useState('');
  const [attachments, setAttachments] = useState<VendorAttachment[]>([]);

  // Extra fields — only present when this vendor is linked to a system
  // Vendors Data List that has customColumns configured. Only usable in
  // edit mode (the values need a real vendorId to attach to) — matches
  // ProductEditScreen.tsx's identical limitation.
  const [customColumns, setCustomColumns] = useState<DataListColumn[]>([]);
  const [customValues, setCustomValues] = useState<Record<string, any>>({});
  const [customValueErrors, setCustomValueErrors] = useState<Record<string, string>>({});
  const [savingCustomValues, setSavingCustomValues] = useState(false);


  useEffect(() => {
    if (isEdit) loadVendor();
  }, [vendorId]);

  // "Add as vendor" from a contact profile (or a visit there) — start the
  // new vendor from the contact's details.
  useEffect(() => {
    const prefill = routeParams?.prefillVendor;
    if (isEdit || !prefill) return;
    if (prefill.name) setName(prefill.name);
    if (prefill.phone) setPhone(prefill.phone);
    if (prefill.email) setEmail(prefill.email);
    if (prefill.address) setAddress(prefill.address);
    if (typeof prefill.location?.latitude === 'number' && typeof prefill.location?.longitude === 'number') {
      setLocation({
        latitude: prefill.location.latitude,
        longitude: prefill.location.longitude,
        address: prefill.location.address || prefill.address || '',
        city: prefill.location.city,
        country: prefill.location.country,
      } as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  useEffect(() => {
    if (!listId || !isEdit || !vendorId) return;
    apiService.getDataList(listId).then((res) => {
      if (res.statusCode === 1000 && res.data?.customColumns?.length) {
        setCustomColumns(res.data.customColumns);
      }
    }).catch((err) => console.error('[VendorFormScreen] Failed to load linked list:', err));
    apiService.getDataListRecord(listId, vendorId).then((res) => {
      if (res.statusCode === 1000 && res.data) {
        setCustomValues(res.data.values || {});
      }
    }).catch((err) => console.error('[VendorFormScreen] Failed to load custom field values:', err));
  }, [listId, isEdit, vendorId]);

  const handleSaveCustomValues = async () => {
    if (!listId || !vendorId) return;
    try {
      setSavingCustomValues(true);
      const res = await apiService.upsertDataListRecordCustomValues(listId, vendorId, customValues);
      if (res.statusCode === 1000) {
        showNotification('success', 'Success', 'Extra fields updated successfully.');
        setCustomValueErrors({});
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to update extra fields');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to update extra fields');
    } finally {
      setSavingCustomValues(false);
    }
  };

  const loadVendor = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVendorById(vendorId!);
      if (response.statusCode === 1000 && response.data) {
        const v = response.data;
        setName(v.name || '');
        setPhone(v.phone || '');
        setEmail(v.email || '');
        setAddress(v.address || '');
        setLocation(v.location || null);
        setNotes(parseNotes(v.notes));
        setCrNumber(v.crNumber || '');
        setVatNumber(v.vatNumber || '');
        setMinimumOrderAmount(v.minimumOrderAmount ? String(v.minimumOrderAmount) : '');
        setMinimumOrderCurrency(v.minimumOrderCurrency || '');
        setAttachments(v.attachments || []);
      } else {
        showNotification('error', 'Error', 'Vendor not found');
        navigateTo('vendors');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to load vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showNotification('warning', 'Missing Name', 'Vendor name is required');
      return;
    }
    const minAmountText = minimumOrderAmount.trim().replace(',', '.');
    const minAmount = minAmountText ? Number(minAmountText) : 0;
    if (minAmountText && (!Number.isFinite(minAmount) || minAmount < 0)) {
      showNotification('warning', t('vendors.minimumOrder.invalidTitle'), t('vendors.minimumOrder.invalidAmount'));
      return;
    }
    try {
      setSaving(true);
      const validNotes = notes.filter((note) => note && note.content && note.content.trim());
      const payload = {
        name: name.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        address: address.trim() || undefined,
        location: location || undefined,
        notes: validNotes.length > 0 ? JSON.stringify(validNotes) : undefined,
        crNumber: crNumber.trim() || undefined,
        vatNumber: vatNumber.trim() || undefined,
        // null clears an existing minimum on edit.
        minimumOrderAmount: minAmount > 0 ? minAmount : (isEdit ? null : undefined),
        minimumOrderCurrency: minAmount > 0 ? (minimumOrderCurrency || currentBusiness?.currency || undefined) : (isEdit ? null : undefined),
      };
      const response = isEdit
        ? await apiService.updateVendor(vendorId!, payload)
        : await apiService.createVendor({
            ...payload,
            // "Add as vendor" from a contact (optionally during a visit).
            ...(routeParams?.prefillVendor?.contactId ? { contactId: String(routeParams.prefillVendor.contactId) } : {}),
            ...(routeParams?.prefillVendor?.visitId ? { visitId: String(routeParams.prefillVendor.visitId) } : {}),
          });

      if (response.statusCode === 1000 && response.data) {
        showNotification('success', 'Saved', `Vendor ${isEdit ? 'updated' : 'created'} successfully`);
        const newVendorId = response.data._id || vendorId;
        // Newly-created vendor, reached via a scoped Vendors Data List —
        // link it to that list (membership), mirroring
        // ProductCreateScreen.tsx's identical handling exactly. Extra field
        // VALUES still need a follow-up edit (same limitation as Products),
        // but this at least makes the vendor show up in that list at all.
        if (!isEdit && listId && newVendorId) {
          try {
            await apiService.linkDataListRecords(listId, [newVendorId]);
          } catch (err) {
            console.error('[VendorFormScreen] Failed to link new vendor to data list:', err);
          }
          navigateTo('data-list-records', listId);
        } else {
          navigateTo('vendor-detail', newVendorId, listId ? { listId } : undefined);
        }
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to save vendor');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleAttachFile = async () => {
    if (!isEdit) {
      showNotification('warning', 'Save first', 'Save the vendor before attaching files');
      return;
    }
    try {
      const result = await DocumentPicker.getDocumentAsync({ type: '*/*', copyToCacheDirectory: true });
      if (result.canceled || !result.assets?.[0]) return;
      const file = result.assets[0];

      setUploadingFile(true);
      const response = await apiService.uploadVendorAttachment(vendorId!, file.uri, file.name, file.mimeType || 'application/octet-stream');
      if (response.statusCode === 1000 && response.data) {
        setAttachments(response.data.attachments || []);
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to upload file');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveAttachment = async (url: string) => {
    if (!isEdit) return;
    try {
      const response = await apiService.removeVendorAttachment(vendorId!, url);
      if (response.statusCode === 1000 && response.data) {
        setAttachments(response.data.attachments || []);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to remove attachment');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title={isEdit ? 'Edit Vendor' : 'New Vendor'}
          onBack={() => navigateTo(isEdit ? 'vendor-detail' : 'vendors', vendorId)}
          actions={[{ icon: 'checkmark', primary: true, onPress: handleSave, loading: saving, disabled: saving, accessibilityLabel: 'Save' }]}
        />
      }
    >
      <View style={styles.container}>
        {loading ? (
          <View style={styles.centerContainer}><ActivityIndicator size="large" color={colors.primary} /></View>
        ) : (
          <ScrollView style={styles.content} contentContainerStyle={styles.contentInner}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>Basic Info</Text>
            <Input label="Vendor Name *" value={name} onChangeText={setName} placeholder="e.g. Acme Supplies Co." />
            <Input label="Phone" value={phone} onChangeText={setPhone} placeholder="+1234567890" latin />
            <Input label="Email" value={email} onChangeText={setEmail} placeholder="vendor@example.com" latin autoCapitalize="none" />

            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, marginTop: spacing.lg }]}>Address</Text>
            <Input label="Address" value={address} onChangeText={setAddress} placeholder="Street, city, country" multiline />
            <TouchableOpacity style={[styles.mapButton, { flexDirection: rtl.rowDirection }]} onPress={() => setShowLocationPicker(true)}>
              <Ionicons name="location-outline" size={18} color={colors.primary} />
              <Text style={styles.mapButtonText}>{location ? 'Change pinned location' : 'Pin location on map'}</Text>
            </TouchableOpacity>
            {location && (
              <Text style={styles.locationHint} numberOfLines={1}>{location.address}</Text>
            )}

            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, marginTop: spacing.lg }]}>Registration</Text>
            <Input label="CR Number" value={crNumber} onChangeText={setCrNumber} placeholder="Commercial Registration number" latin />
            <Input label="VAT Number" value={vatNumber} onChangeText={setVatNumber} placeholder="VAT registration number" latin />

            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart, marginTop: spacing.lg }]}>{t('vendors.minimumOrder.section')}</Text>
            <Text style={[styles.locationHint, { textAlign: rtl.textStart, marginBottom: spacing.sm }]}>{t('vendors.minimumOrder.hint')}</Text>
            <View style={{ flexDirection: rtl.rowDirection, gap: spacing.sm }}>
              <View style={{ flex: 1 }}>
                <Input
                  label={t('vendors.minimumOrder.amount')}
                  value={minimumOrderAmount}
                  onChangeText={(v) => setMinimumOrderAmount(v.replace(/[^0-9.,]/g, ''))}
                  placeholder={t('vendors.minimumOrder.none')}
                  keyboardType="decimal-pad"
                  latin
                />
              </View>
              <View style={{ width: 140 }}>
                <CurrencyPicker
                  label={t('vendors.minimumOrder.currency')}
                  value={minimumOrderCurrency || currentBusiness?.currency || 'USD'}
                  onChange={setMinimumOrderCurrency}
                />
              </View>
            </View>

            <View style={{ marginTop: spacing.lg }}>
              <NotesSection
                notes={notes}
                onAddNote={(content) => {
                  if (!content.trim()) return;
                  setNotes((prev) => [...prev, {
                    content: content.trim(),
                    createdBy: {
                      id: user?.id || '',
                      _id: user?.id || '',
                      firstName: user?.firstName,
                      lastName: user?.lastName,
                      email: user?.email,
                      avatar: user?.avatar,
                      profilePicture: user?.profilePicture,
                    },
                    createdAt: new Date(),
                  }]);
                }}
                onDeleteNote={(noteId) => {
                  setNotes((prev) => prev.filter((note, index) => (
                    typeof noteId === 'string' ? note._id !== noteId : index !== noteId
                  )));
                }}
                placeholder="Add an internal note about this vendor..."
                title="Notes"
                editable
                currentUserId={user?.id}
              />
            </View>

            <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection, marginTop: spacing.lg }]}>
              <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>Files (CR, VAT, other documents)</Text>
              <TouchableOpacity style={[styles.addFileButton, { flexDirection: rtl.rowDirection }]} onPress={handleAttachFile} disabled={uploadingFile}>
                {uploadingFile ? <ActivityIndicator size="small" color={colors.primary} /> : <Ionicons name="add" size={18} color={colors.primary} />}
                <Text style={styles.addFileText}>Attach File</Text>
              </TouchableOpacity>
            </View>
            {!isEdit && (
              <Text style={styles.hint}>Save the vendor first to attach files.</Text>
            )}
            {attachments.map((att) => (
              <View key={att.url} style={[styles.attachmentRow, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="document-outline" size={18} color={colors.textLight} />
                <Text style={styles.attachmentName} numberOfLines={1}>{att.name}</Text>
                <TouchableOpacity onPress={() => handleRemoveAttachment(att.url)}>
                  <Ionicons name="close-circle" size={18} color={colors.error} />
                </TouchableOpacity>
              </View>
            ))}

            {/* Extra Fields — custom columns on the linked system Vendors
                Data List, alongside this vendor's own fixed fields above.
                Only shown when reached via that list's scoped mode AND it
                has customColumns configured. Mirrors ProductEditScreen.tsx
                exactly. */}
            {listId && customColumns.length > 0 && currentBusiness?._id && (
              <View style={styles.customFieldsSection}>
                <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>Extra Fields</Text>
                {customColumns.map((col) => (
                  <DataListFieldRenderer
                    key={col.id}
                    column={col}
                    value={customValues[col.id]}
                    onChange={(v) => setCustomValues((prev) => ({ ...prev, [col.id]: v }))}
                    error={customValueErrors[col.id]}
                    businessId={currentBusiness._id}
                    listId={listId}
                    recordId={vendorId}
                  />
                ))}
                <Button
                  title="Save Extra Fields"
                  onPress={handleSaveCustomValues}
                  loading={savingCustomValues}
                  variant="secondary"
                />
              </View>
            )}
          </ScrollView>
        )}
      </View>

      <MapLocationPicker
        visible={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={(loc) => { setLocation(loc); setShowLocationPicker(false); }}
        initialLocation={location}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.base },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  content: { flex: 1 },
  contentInner: { padding: spacing.lg, paddingBottom: Platform.OS !== 'web' ? 120 : spacing.xl },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  sectionHeader: { alignItems: 'center', justifyContent: 'space-between' },
  mapButton: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
  },
  mapButtonText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.medium },
  locationHint: { fontSize: typography.fontSize.sm, color: colors.textLight, marginBottom: spacing.sm },
  addFileButton: { alignItems: 'center', gap: spacing.xs },
  addFileText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.medium },
  hint: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: spacing.xs },
  attachmentRow: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  attachmentName: { flex: 1, fontSize: typography.fontSize.sm, color: colors.textDark },
  customFieldsSection: {
    marginTop: spacing.lg,
    padding: spacing.md,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
});
